import { z } from 'zod';
import { idSchema, instantSchema } from './common.js';
/**
 * Domain event names — the closed set of things the CRM can say happened.
 *
 * Naming: `crm.<aggregate>.<past-tense verb>`. Dotted so a subscriber can match a prefix
 * (`crm.candidate.*`), past tense because an event is a fact, not a command. A name lives
 * here BEFORE any code emits it, so the audit log, webhooks and the web app agree on the
 * spelling; a typo'd event name in a publisher is a compile error, not a silent no-op.
 *
 * Payload schemas sit next to the names so `EventEnvelope` can be validated at the edge
 * (inbound webhook replay, outbox drain) with the payload narrowed by name.
 */
/** The record kinds whose lifecycle is published (singular, as in the event name). */
export const RECORD_EVENT_ENTITIES = [
    'candidate',
    'company',
    'contact',
    'project',
    'deal',
    'job',
    // TS-1833: the RecruitCRM sync (TS-1831) has to hear when a note changes. It reads the note
    // and its record fresh, so the note's id in the event is all it needs.
    'note',
];
export const RECORD_EVENT_ACTIONS = ['created', 'updated', 'deleted', 'restored'];
function RECORD_EVENT_NAMES() {
    return RECORD_EVENT_ENTITIES.flatMap((e) => RECORD_EVENT_ACTIONS.map((a) => `crm.${e}.${a}`));
}
/** Audit `entity_type` (table name) → the entity in the event name. Others publish nothing. */
export const AUDIT_ENTITY_EVENT = {
    candidates: 'candidate',
    companies: 'company',
    contacts: 'contact',
    projects: 'project',
    deals: 'deal',
    jobs: 'job',
    notes: 'note',
};
/**
 * Record kinds whose lifecycle events carry the id only: `changes` is always null. A note's body
 * is free text about a person (candidate PII), and `changes` goes to every `*` webhook and to
 * SQS. A subscriber that needs the content reads the record. TS-1833 (Ankush's review).
 */
export const ID_ONLY_EVENT_ENTITIES = new Set(['note']);
/** Audit action → event verb. An action not listed (download, merge…) publishes nothing. */
export const AUDIT_ACTION_EVENT = {
    create: 'created',
    update: 'updated',
    delete: 'deleted',
    restore: 'restored',
};
export const EVENT_NAMES = [
    'crm.candidate.archived',
    'crm.customer.created',
    'crm.customer.updated',
    'crm.customer.archived',
    'crm.field_definition.changed',
    'crm.feature_flag.changed',
    'crm.audit.recorded',
    // Record lifecycle (TS-958): one per audited create / update / delete / restore, emitted from
    // the audit write itself so no route can change a record without saying so.
    ...RECORD_EVENT_NAMES(),
    // Stage moves, emitted where the stage history is written (TS-958). A project's carries the
    // pipeline it moved in (`data.pipelineId`, TS-1747): it sits in several at once.
    'crm.project.stage_changed',
    // A project joining or leaving a pipeline (TS-1747); `data` names the pipeline and the stage.
    'crm.project.pipeline_added',
    'crm.project.pipeline_removed',
    // A lost place in a pipeline folded away, or brought back; `data` as above.
    'crm.project.pipeline_archived',
    'crm.project.pipeline_restored',
    // A deal's or job's move in a pipeline (its default status, or an extra pipeline's stage, TS-1956):
    // `data.pipelineId` says which.
    'crm.deal.status_changed',
    'crm.job.status_changed',
    // A deal or job joining an extra pipeline (TS-1964); `data` names the pipeline and the stage.
    'crm.deal.pipeline_added',
    'crm.job.pipeline_added',
    // A move on a SHORTLIST (Shortlist Flow, RELAY-951/TS-1888): `data` names the job and shortlist.
    'crm.candidate.stage_changed',
    // A candidate's own place in a Candidate Flow pipeline (not a shortlist): a move in one, and
    // joining, archiving or restoring one; `data` names the pipeline and the stage(s). A move in
    // the DEFAULT pipeline also changes `candidates.stage`, so it comes with crm.candidate.updated.
    'crm.candidate.pipeline_stage_changed',
    'crm.candidate.pipeline_added',
    'crm.candidate.pipeline_archived',
    'crm.candidate.pipeline_restored',
    /** A delivery check from Settings → Webhooks: proves the URL and the signature, changes nothing. */
    'crm.webhook.pinged',
];
export const eventNameSchema = z.enum(EVENT_NAMES);
/** Who caused the event. `system` is for scheduled and reactive work with no user. */
export const actorSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('user'), userId: idSchema }),
    z.object({ kind: z.literal('system'), job: z.string().min(1) }),
    z.object({ kind: z.literal('integration'), name: z.string().min(1) }),
]);
const entityRef = z.object({ id: idSchema });
const changeSet = z.object({
    changed: z.array(z.string().min(1)).min(1),
});
/** Payload per event name. Every name in EVENT_NAMES must have an entry (enforced by type). */
/**
 * The payload of every record and stage event (TS-958), and so the body subscribers receive:
 * what the record is, `data` (its id plus, for a stage move, the stages and their names), and
 * `changes` — the audit diff `{ field: { old, new } }`, secrets already redacted. A subscriber
 * that needs the whole record reads it from the API with its key; the event says what moved.
 */
export const recordEventPayloadSchema = z.object({
    entityType: z.string().min(1),
    id: idSchema,
    data: z.record(z.string(), z.unknown()),
    changes: z
        .record(z.string(), z.object({ old: z.unknown().optional(), new: z.unknown().optional() }))
        .nullable(),
});
export const EVENT_PAYLOADS = {
    ...Object.fromEntries(RECORD_EVENT_NAMES().map((n) => [n, recordEventPayloadSchema])),
    'crm.project.stage_changed': recordEventPayloadSchema,
    'crm.project.pipeline_added': recordEventPayloadSchema,
    'crm.project.pipeline_removed': recordEventPayloadSchema,
    'crm.project.pipeline_archived': recordEventPayloadSchema,
    'crm.project.pipeline_restored': recordEventPayloadSchema,
    'crm.deal.status_changed': recordEventPayloadSchema,
    'crm.job.status_changed': recordEventPayloadSchema,
    'crm.deal.pipeline_added': recordEventPayloadSchema,
    'crm.job.pipeline_added': recordEventPayloadSchema,
    'crm.candidate.stage_changed': recordEventPayloadSchema,
    'crm.candidate.pipeline_stage_changed': recordEventPayloadSchema,
    'crm.candidate.pipeline_added': recordEventPayloadSchema,
    'crm.candidate.pipeline_archived': recordEventPayloadSchema,
    'crm.candidate.pipeline_restored': recordEventPayloadSchema,
    'crm.webhook.pinged': recordEventPayloadSchema,
    'crm.candidate.archived': entityRef.extend({ reason: z.string().max(500).optional() }),
    'crm.customer.created': entityRef,
    'crm.customer.updated': entityRef.extend(changeSet.shape),
    'crm.customer.archived': entityRef.extend({ reason: z.string().max(500).optional() }),
    'crm.field_definition.changed': z.object({
        entityKind: z.string().min(1),
        key: z.string().min(1),
        action: z.enum(['created', 'updated', 'deleted']),
    }),
    'crm.feature_flag.changed': z.object({
        flag: z.string().min(1),
        enabled: z.boolean(),
    }),
    'crm.audit.recorded': z.object({ auditId: idSchema }),
};
/** The envelope every event travels in — on the outbox table, on the wire, in the audit log. */
export const eventEnvelopeSchema = z.object({
    id: idSchema,
    name: eventNameSchema,
    occurredAt: instantSchema,
    actor: actorSchema,
    payload: z.unknown(),
});
/**
 * Parse an envelope AND its payload against the schema for its name. A structurally valid
 * envelope carrying the wrong payload for its name is rejected — the case an outbox replay
 * after a schema change would otherwise let through.
 */
export function parseEvent(input) {
    const envelope = eventEnvelopeSchema.safeParse(input);
    if (!envelope.success)
        return envelope;
    const payload = EVENT_PAYLOADS[envelope.data.name].safeParse(envelope.data.payload);
    if (!payload.success)
        return payload;
    return { success: true, data: { ...envelope.data, payload: payload.data } };
}
//# sourceMappingURL=events.js.map